import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import type {ElementData} from '@Libraries/add-media-library';
import {Panel, PanelContent, PanelHeader} from '@Components/panel';
import {MultiMediaUploadSource} from '@Libraries/user-media-library';
import {MediaImagePanelContent} from '@Panels/media-type-panel/components/media-image-panel-content';
import {MediaVideoPanelContent} from '@Panels/media-type-panel/components/media-video-panel-content';
import {updatePanelSizeData} from '@Components/panel/panel-reducer';
import useWindowSize from '@Hooks/useWindowSize';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {useDefaultPanelId} from '@Panels/hooks/useDefaultPanelId';
import {type TabKey, Tabs} from '@Components/tabs';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {StockSources} from '@Libraries/pmw-stock-media-library';
import type {StockPanelTabs} from '@Panels/stock-panel/stock-panel.types';
import {Icon} from '@Components/icon-v2';
import {deleteTabStateForId} from '@Components/tabs/tabs-reducer';
import {useAppDispatch} from '@/hooks';
import styles from './media-type-panel.module.scss';

export enum MediaType {
  IMAGE = 'image',
  VIDEO = 'video',
}

export interface MediaTypePanelProps {
  onAddSelected(items: Array<ElementData>): void;

  mediaType?: MediaType;
  title?: string;
  panelId?: string;
  uploadSource?: MultiMediaUploadSource;
  maxSelectionNumber?: number;
  showVectors?: boolean;
  addInBulk?: boolean;
  videoQualityFiltersDisabled?: boolean;
  showEmbedVideo?: boolean;
  onAddEmbeddedVideo?(videoURL: string, thumbnailURL: string): void;
  onVideoUploadRequest?: () => void;
  onVideoUploadError?(errorMessage: string): void;
  hideRecordingOptions?: boolean;
  onAddBrandAsset?(): void;
  onBack?(): void;
  blacklistedSources?: Array<StockSources>;
  /** Overrides the tabs the image content's Explore-media option opens the stock panel with. A single tab opens it in grid view. */
  exploreMediaStockTabs?: StockPanelTabs[];
  /** Which AI generator the image content's AI-images option opens on mobile — 'ai-stickers' preselects the Sticker media type. */
  aiImagesPanelKey?: 'ai-images' | 'ai-stickers';
  /** Overrides what the image content's Explore-media option opens (e.g. the stickers grid for sticker components). */
  onExploreMedia?: () => void;
  myDesignSelectedCallback?: VoidFunction;
  hideFooterSecondaryActions?: boolean;
  onStartAddSelected?: (numItems?: number) => void;
}

const TABS_ID = 'media-type-tabs';

function MediaTypePanel({
  uploadSource = MultiMediaUploadSource.EMAIL_UPLOAD,
  showEmbedVideo = false,
  hideRecordingOptions = false,
  showVectors = true,
  videoQualityFiltersDisabled = false,
  ...props
}: MediaTypePanelProps): ReactElement<any> {
  const dispatch = useAppDispatch();

  const panelId = useDefaultPanelId(props.panelId);

  const panelContentRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const {windowWidth} = useWindowSize();
  const onClose = useDefaultPanelClose(panelId);

  const [defaultMediaType, setDefaultMediaType] = useState<MediaType>(props.mediaType ? props.mediaType : MediaType.IMAGE);

  const getPanelWidth = (): number => {
    return defaultMediaType === MediaType.IMAGE && !props.myDesignSelectedCallback ? 612 : 788;
  };

  useEffect(() => {
    if (windowWidth < 535) {
      setIsMobile(true);
      dispatch(
        updatePanelSizeData({
          sizeDataId: panelId,
          sizeData: {
            height: `fit-content`,
          },
        })
      );
    } else {
      setIsMobile(false);
    }
  }, [windowWidth]);

  useEffect(() => {
    dispatch(
      updatePanelSizeData({
        sizeDataId: panelId,
        sizeData: {
          width: `${getPanelWidth()}px`,
        },
      })
    );

    return (): void => {
      dispatch(deleteTabStateForId(TABS_ID));
    };
  }, []);

  const getContent = (): ReactElement<any> | undefined => {
    if (defaultMediaType === MediaType.IMAGE) {
      return (
        <MediaImagePanelContent
          isMobile={isMobile}
          panelRef={panelContentRef}
          onClose={onClose}
          panelId={props.panelId}
          showVectors={showVectors}
          maxSelectionNumber={props.maxSelectionNumber}
          hideRecordingOptions={hideRecordingOptions}
          addInBulk={props.addInBulk}
          uploadSource={uploadSource}
          onAddBrandAsset={props.onAddBrandAsset}
          onAddSelected={props.onAddSelected}
          onStartAddSelected={props.onStartAddSelected}
          blacklistedSources={props.blacklistedSources}
          exploreMediaStockTabs={props.exploreMediaStockTabs}
          aiImagesPanelKey={props.aiImagesPanelKey}
          onExploreMedia={props.onExploreMedia}
          myDesignSelectedCallback={props.myDesignSelectedCallback}
          hideMyUploadsFooterSecondaryActions={isMobile}
        />
      );
    }
    if (defaultMediaType === MediaType.VIDEO) {
      return (
        <MediaVideoPanelContent
          isMobile={isMobile}
          panelRef={panelContentRef}
          onClose={onClose}
          panelId={props.panelId}
          onAddEmbeddedVideo={props.onAddEmbeddedVideo}
          onVideoUploadRequest={props.onVideoUploadRequest}
          onVideoUploadError={props.onVideoUploadError}
          showVectors={showVectors}
          maxSelectionNumber={props.maxSelectionNumber}
          hideRecordingOptions={hideRecordingOptions}
          addInBulk={props.addInBulk}
          uploadSource={uploadSource}
          onAddBrandAsset={props.onAddBrandAsset}
          onAddSelected={props.onAddSelected}
          showEmbedVideo={showEmbedVideo}
          videoQualityFiltersDisabled={videoQualityFiltersDisabled}
          blacklistedSources={props.blacklistedSources}
        />
      );
    }

    return undefined;
  };

  const getPanelContent = (): ReactElement<any> => {
    return <PanelContent>{getContent()}</PanelContent>;
  };

  const getPanelHeader = (): ReactElement<any> => {
    if (props.mediaType) {
      return <PanelHeader onClose={onClose} title={props.title} />;
    }

    return (
      <div className={`_full-width flex-center ${styles.header}`}>
        <Tabs
          onTabClick={(tabKey: TabKey) => {
            if (tabKey === 'image') {
              setDefaultMediaType(MediaType.IMAGE);
            } else if (tabKey === 'video') {
              setDefaultMediaType(MediaType.VIDEO);
            }
          }}
          className={`spacing-p-t-3 spacing-m-0 ${styles.tabs}`}
          id={TABS_ID}
          key={TABS_ID}
          tabItems={[
            {
              containerClassName: styles.tabItem,
              tabKey: MediaType.IMAGE,
              text: window.i18next.t('pmwjs_image'),
            },
            {
              tabKey: MediaType.VIDEO,
              text: window.i18next.t('pmwjs_video'),
            },
          ]}
          selectedTab={defaultMediaType}
        />
        <Icon
          className={styles.closeIcon}
          type={IconType.GHOST}
          shape={IconShape.SQUARE}
          key="panel-header-close-button"
          onClick={onClose}
          icon="icon-close"
          size={IconSize.SIZE_ICON_24}
        />
      </div>
    );
  };

  return <Panel className="border-s-standard" panelHeader={getPanelHeader()} panelContent={getPanelContent()} />;
}

export default MediaTypePanel;
